import { createContext, useContext } from "react";
import type { ReactNode, SVGProps } from "react";
import { cx } from "./utils";

/**
 * Icon ids available in the design system sprite
 * (`assets/brand-v2/semantic-icons.svg`, see `icon-catalog.json`).
 */
export type IconId =
  | "ui-microphone"
  | "ui-record"
  | "ui-play"
  | "ui-pause"
  | "ui-stop"
  | "ui-check"
  | "ui-check-circle"
  | "ui-close"
  | "ui-info"
  | "ui-warning"
  | "ui-error"
  | "ui-arrow-left"
  | "ui-arrow-right"
  | "ui-arrow-up"
  | "ui-arrow-down"
  | "ui-chevron-left"
  | "ui-chevron-right"
  | "ui-chevron-up"
  | "ui-chevron-down"
  | "ui-download"
  | "ui-upload"
  | "ui-refresh"
  | "ui-undo"
  | "ui-redo"
  | "ui-repeat"
  | "ui-search"
  | "ui-filter"
  | "ui-settings"
  | "ui-moon"
  | "ui-sun"
  | "ui-menu"
  | "ui-more"
  | "ui-list"
  | "ui-list-ordered"
  | "ui-user"
  | "ui-users"
  | "ui-user-switch"
  | "ui-logout"
  | "ui-lock"
  | "ui-shield"
  | "ui-key"
  | "ui-home"
  | "ui-document"
  | "ui-folder"
  | "ui-inbox"
  | "ui-clock"
  | "ui-history"
  | "ui-calendar"
  | "ui-chart-line"
  | "ui-percent"
  | "ui-target"
  | "ui-star"
  | "ui-grade"
  | "ui-eye"
  | "ui-save"
  | "ui-edit"
  | "ui-trash"
  | "ui-reading"
  | "ui-listening"
  | "ui-speaking"
  | "ui-writing"
  | "ui-help"
  | "ui-loading"
  | "ui-school"
  | "ui-tag"
  | "ui-hash"
  | "ui-expand"
  | "ui-collapse"
  | "ui-sidebar-collapse"
  | "ui-sidebar-expand"
  | "ui-swap"
  | "ui-select"
  | "ui-ban"
  | "ui-notification"
  | "ui-globe"
  | "ui-language";

/** Default sprite URL; the runtime is served from `/design-system/`. */
export const DEFAULT_SPRITE_BASE = "/design-system/assets/brand-v2/semantic-icons.svg";

const SpriteCtx = createContext<string>(DEFAULT_SPRITE_BASE);

/** Override the sprite URL for a subtree (tests, sub-path deployments). */
export function IconProvider({
  spriteBase,
  children,
}: {
  spriteBase: string;
  children: ReactNode;
}) {
  return <SpriteCtx.Provider value={spriteBase}>{children}</SpriteCtx.Provider>;
}

/** Current sprite base URL. */
export function useSpriteBase(): string {
  return useContext(SpriteCtx);
}

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, "children"> {
  /** Sprite symbol id. */
  id: IconId;
  /** Maps to `data-size` on the svg. @default 20 */
  size?: 16 | 20 | 24;
  /**
   * Accessible name. Omit (the default) to render the icon as decorative;
   * provide it only when the icon is the sole carrier of meaning.
   */
  title?: string;
}

/**
 * Renders one symbol from the design system sprite. There is no second icon
 * set: every glyph in the app comes from `semantic-icons.svg`.
 */
export function Icon({ id, size = 20, title, className, ...rest }: IconProps) {
  const base = useSpriteBase();
  const decorative = title == null;
  return (
    <svg
      className={cx("gz-icon", className)}
      data-size={size}
      aria-hidden={decorative ? true : undefined}
      role={decorative ? undefined : "img"}
      aria-label={decorative ? undefined : title}
      focusable="false"
      {...rest}
    >
      <use href={`${base}#${id}`} />
    </svg>
  );
}
